/**
 * The service reports (LIAA service plan S10 — P1-17): the screen's table (`POST /reports/preview`, computed exactly as the
 * export is), the exports (`POST /reports/generate` — a job; its file downloaded from the job's route) and their history
 * (`GET /reports`, the keys the user may see).
 */

import { apiClient } from '@/shared/api/client';

export type ServiceReportKey = 'service-workload' | 'service-revenue';
export type ReportFormat = 'xlsx' | 'pdf';

export interface ServiceReportFilters {
  mode?: 'staff' | 'resources';
  workTypeId?: string;
  language?: 'lv' | 'en';
}

export interface ReportRequest {
  key: ServiceReportKey;
  format: ReportFormat;
  period: { from: string; to: string };
  filters: ServiceReportFilters;
}

export type Cell = string | number | null;

export interface ReportResult {
  key: string;
  format: ReportFormat;
  columns: string[];
  rows: Array<Record<string, Cell>>;
  summary: Record<string, number | null>;
  sections?: Array<{ key: string; columns: string[]; rows: Array<Record<string, Cell>>; summary: Record<string, number | null> }>;
}

export interface ReportJob {
  id: string;
  key: string;
  format: ReportFormat;
  status: 'queued' | 'running' | 'completed' | 'failed';
  period: { from: string; to: string };
  filters: ServiceReportFilters;
  downloadPath: string | null;
  downloadExpiresAt: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function previewReport(body: Omit<ReportRequest, 'format'>): Promise<ReportResult> {
  return (await apiClient.post<{ report: ReportResult }>('/reports/preview', { ...body, format: 'xlsx' })).data.report;
}

export async function generateReport(body: ReportRequest): Promise<ReportJob> {
  return (await apiClient.post<{ job: ReportJob }>('/reports/generate', body, { headers: { 'Idempotency-Key': crypto.randomUUID() } })).data.job;
}

export const JOBS_PAGE = 20;

/** One page of the service's exports, newest first, and how many there are (the server keeps to the keys). */
export async function fetchReportJobs(page: number): Promise<{ jobs: ReportJob[]; total: number }> {
  const { data } = await apiClient.get<{ data: ReportJob[]; meta: { totalItems: number } }>('/reports', {
    params: { page, limit: JOBS_PAGE, keys: 'service-workload,service-revenue' },
  });
  return { jobs: data.data, total: data.meta.totalItems };
}

/** One export's state (an export started here is followed by its own route — wherever it sits in the history). */
export async function fetchReportJob(id: string): Promise<ReportJob> {
  return (await apiClient.get<{ job: ReportJob }>(`/reports/${id}/status`)).data.job;
}

/** A completed export whose download link has run out (the server keeps it 15 minutes). */
export function expired(job: ReportJob, now: number): boolean {
  return job.status === 'completed' && job.downloadExpiresAt !== null && Date.parse(job.downloadExpiresAt) <= now;
}

/** The job's file, through the served route (same origin, the session's cookie). */
export function downloadHref(job: ReportJob): string {
  return `/api/v1/reports/${job.id}/download`;
}
